import AnnotationType from "./AnnotationType";
import AnnotationCC from "../../api/AnnotationCommunicationController";

export type AnnotationToolEndpoints = {
    add_storage_endpoint: string;
    add_webhook_endpoint: string;
    create_task_endpoint: string;
    delete_task_endpoint: string;
    edit_annotation_endpoint: string;
    import_media_endpoint: string;
    task_homepage_endpoint: string;
    update_annotation_endpoint: string;
};

export type EndpointsKey = keyof AnnotationToolEndpoints;

export const isEndpointValid = (endpoint: string) => {
    return endpoint.trim() !== "";
};

export const isValidHttpUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
};

// base_url ora è sempre obbligatorio (non più vuoto per un tool integrato): la
// distinzione integrato/esterno è nella FORMA del valore, non nella sua presenza —
// assoluto (http/https) per un tool esterno, un percorso assoluto dell'app (che
// parte da "/") per un tool integrato, es. "/atom/segmentation". Questo
// richiede anche una modifica lato server (non applicata in questa sessione, solo
// descritta): TaskController.ts distingueva finora esterno/interno guardando se
// base_url fosse "truthy" ("if (tool.base_url) {...}") — con base_url sempre
// valorizzato quel controllo va sostituito con lo stesso controllo di forma fatto
// qui (assoluto vs relativo), altrimenti un tool integrato verrebbe trattato come
// esterno lato server.
export const isValidInternalPath = (path: string): boolean => {
    return path.startsWith("/") && path.trim().length > 1;
};

export const isAnnotationToolDataValid = (data: AnnotationToolData, isExternal: boolean) => {
    if (data.name.trim() === "") return false;

    if (isExternal) {
        if (!isValidHttpUrl(data.base_url)) return false;
        for (const key in data.endpoints) if (!isEndpointValid(data.endpoints[key as EndpointsKey])) return false;
        return true;
    }

    return isValidInternalPath(data.base_url);
};

export class AnnotationToolData {
    name: string = "";
    base_url: string = "";
    authorization_header: string = "";
    new_instance_instructions: string = "";
    endpoints: AnnotationToolEndpoints = {
        create_task_endpoint: "",
        delete_task_endpoint: "",
        task_homepage_endpoint: "",
        import_media_endpoint: "",
        add_storage_endpoint: "",
        add_webhook_endpoint: "",
        update_annotation_endpoint: "",
        edit_annotation_endpoint: ""
    };
}

export type AnnotationToolDataKey = keyof AnnotationToolData;

export default class AnnotationTool {
    id: number;
    name: string;
    base_url: string;
    authorization_header: string;
    new_instance_instructions: string;

    endpoints: AnnotationToolEndpoints;

    annotationTypes: AnnotationType[] = [];
    private _typesSynchronized: boolean = false;

    constructor(obj: AnnotationTool) {
        this.id = obj.id;
        this.name = obj.name;
        this.base_url = obj.base_url;
        this.authorization_header = obj.authorization_header;
        this.new_instance_instructions = obj.new_instance_instructions;
        this.endpoints = obj.endpoints;
    }

    getData = (): AnnotationToolData => {
        return {
            name: this.name,
            base_url: this.base_url,
            authorization_header: this.authorization_header,
            new_instance_instructions: this.new_instance_instructions,
            endpoints: this.endpoints
        };
    };

    filter = (filter: string): boolean => {
        return this.name.toLowerCase().includes(filter.toLowerCase());
    };

    fetchTypes = async (): Promise<AnnotationType[]> => {
        if (!this._typesSynchronized) {
            this.annotationTypes = await AnnotationCC.getAnnotationTypes(this.id);
            this._typesSynchronized = true;
        }
        return this.annotationTypes;
    };

    addType = async (data: {
        name: string;
        description: string;
        instructions: string;
        xml: string;
        print_function: string;
        conflict_functions: string[];
    }): Promise<AnnotationType> => {
        const newType = await AnnotationCC.newAnnotationInterface(this.id, data);
        this.annotationTypes.push(newType);
        return newType;
    };

    async deleteType(type: AnnotationType) {
        await AnnotationCC.deleteAnnotationType(this.id, type.id);
        this.annotationTypes = this.annotationTypes.filter(t => t.id !== type.id);
    }

    update = async (data: AnnotationToolData, isExternal: boolean): Promise<boolean> => {
        if (!isAnnotationToolDataValid(data, isExternal) || !this.checkDifferences(data)) return false;

        await AnnotationCC.updateAnnotationTool(this.id, data);

        this.base_url = data.base_url;
        this.authorization_header = data.authorization_header;
        this.new_instance_instructions = data.new_instance_instructions;
        this.endpoints = data.endpoints;
        return true;
    };

    private checkDifferences = (data: AnnotationToolData) => {
        if (this.base_url !== data.base_url) return true;
        if (this.authorization_header !== data.authorization_header) return true;
        if (this.new_instance_instructions !== data.new_instance_instructions) return true;

        if (this.endpoints.create_task_endpoint !== data.endpoints.create_task_endpoint) return true;
        if (this.endpoints.delete_task_endpoint !== data.endpoints.delete_task_endpoint) return true;
        if (this.endpoints.task_homepage_endpoint !== data.endpoints.task_homepage_endpoint) return true;
        if (this.endpoints.import_media_endpoint !== data.endpoints.import_media_endpoint) return true;
        if (this.endpoints.add_storage_endpoint !== data.endpoints.add_storage_endpoint) return true;
        if (this.endpoints.add_webhook_endpoint !== data.endpoints.add_webhook_endpoint) return true;
        if (this.endpoints.update_annotation_endpoint !== data.endpoints.update_annotation_endpoint) return true;
        if (this.endpoints.edit_annotation_endpoint !== data.endpoints.edit_annotation_endpoint) return true;

        return false;
    };
}
